import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiFilterItem, MiIcon, MiInputTextbox } from "@miplatform/components";
import { ICONS_WEB } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { QuantityControl } from "@mibuildweb/components";
import { Box, InputAdornment, Typography } from "@mui/material";
import { EmptySearchResults } from "../../../../../../components/EmptySearchResults/EmptySearchResults";
import { useAddAreaViewModel } from "./useAddAreaViewModel";

export function AddArea({
  open,
  planAreas,
  areaData,
  setAreaData,
  selectedFloor,
}) {
  const { classes, theme } = useStyles();
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 300);

  const { areaTypes, getAreaTypes } = useAddAreaViewModel({
    debouncedSearch,
  });

  useEffect(() => {
    if (open) {
      getAreaTypes();
    }
  }, [debouncedSearch, open]);

  const getExistingItemCount = (item) => {
    return planAreas.filter((area) => area.areaTypeId === item.id).length;
  };

  const getNewItemCount = (item) => {
    return areaData.filter((area) => area.areaTypeId === item.id).length;
  };

  const isSelected = (itemId) => areaData.some((a) => a.areaTypeId === itemId);

  const addNewArea = (item) => {
    return {
      areaTypeId: item.id,
      label: `${item.name} ${
        getExistingItemCount(item) + getNewItemCount(item) + 1
      }`,
      floorId: selectedFloor.id,
      specColumn: item.specColumn,
      areaTypeName: item.name,
    };
  };

  const handleEmptyAreaSelect = (selected, item) => {
    const updatedAreas = [...areaData, addNewArea(item)].filter(
      (val) => selected || val.areaTypeId !== item.id
    );
    setAreaData(updatedAreas);
  };

  const handleDecreaseQty = (e, item) => {
    e.stopPropagation();
    const index = areaData.findIndex((area) => area.areaTypeId === item.id);
    if (index !== -1) {
      const updatedAreaData = [...areaData];
      updatedAreaData.splice(index, 1);
      setAreaData(updatedAreaData);
    }
  };

  const handleIncreaseQty = (e, item) => {
    e.stopPropagation();
    const updatedAreaData = [...areaData, addNewArea(item)];
    setAreaData(updatedAreaData);
  };

  return (
    <Box className={classes.root}>
      <MiInputTextbox
        style={{ padding: theme.spacing(3) }}
        placeholder="Search Area"
        value={search}
        handleChange={(e) => setSearch(e.target.value)}
        name="area-search"
        textfieldProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MiIcon path={mdi[ICONS_WEB.MAGNIFY]} size={0.8} />
            </InputAdornment>
          ),
          endAdornment: search && (
            <InputAdornment position="end" sx={{ cursor: "pointer" }}>
              <MiIcon
                path={mdi[ICONS_WEB.CLOSE]}
                onClick={() => setSearch("")}
              />
            </InputAdornment>
          ),
        }}
      />
      {areaTypes.length === 0 ? (
        <EmptySearchResults />
      ) : (
        <Box className={classes.filterList}>
          {areaTypes.map((item) => (
            <MiFilterItem
              key={item.id}
              keyVal={item.id}
              title={item.name}
              handleClick={() =>
                handleEmptyAreaSelect(!isSelected(item.id), item)
              }
              selected={isSelected(item.id)}
              rightAction={
                isSelected(item.id) && (
                  <Box className={classes.qtyControlBox}>
                    <Typography className={classes.qtyText}>QTY</Typography>
                    <QuantityControl
                      onIncreaseQty={(e) => handleIncreaseQty(e, item)}
                      onDecreaseQty={(e) => handleDecreaseQty(e, item)}
                      quantity={getNewItemCount(item)}
                      variant="circle"
                    />
                  </Box>
                )
              }
              iconPath={ICONS_WEB.CHECK}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(0, 0),
    display: "flex",
    flexDirection: "column",
  },
  filterList: {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  qtyText: {
    borderLeft: `1px solid ${theme.palette.grey.accent}`,
    paddingLeft: theme.spacing(1),
  },
  qtyControlBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
  },
}));
